import { InspectDescriptions } from "@/model/InspectGensha";
import { InspectLineResult } from "@/model/InspectLineResult";
import {
  InspectLineConfirmApplyState,
  InspectLineInitialResultState,
  InspectLinePageService as InspectLinePageServiceInterface,
  InspectLineSeibiState,
  InspectLineSeibiSyncApplyState,
  InspectLineValueConfirmApplyState,
  InspectLineWorkType
} from "@/model/InspectLinePageService";
import requestService from "@/service/requestService";
import {
  buildInspectLineConfirmState,
  buildInspectLineSeibiSyncState,
  buildInspectLineValueConfirmState,
  buildInspectLineWorkBuhinData,
  getInspectLineWorkButtonLabel,
  hasInspectLineDieselCompleted,
  hasInspectLineResult,
  hasInspectLineSpeedMeterCompleted,
  hasInspectLineSpeedValue,
  resolveInspectLineWorkInspect,
  resolveInspectLineWorkInspects
} from "@/components/work-buhin/WorkBuhinInspectLine";

export class InspectLinePageService implements InspectLinePageServiceInterface {
  private reqService: requestService;

  constructor() {
    this.reqService = new requestService();
  }

  getInspectLineResult(uketsukeExtendsId: number): Promise<InspectLineResult[]> {
    return this.reqService.getInspectLineResult(uketsukeExtendsId) as Promise<InspectLineResult[]>;
  }

  getInspectLineSeibi(uketsukeExtendsId: number): Promise<any> {
    return this.reqService.getInspectLineSeibi(uketsukeExtendsId);
  }

  getKbnSkns(sknTitle: string): Promise<any> {
    return this.reqService.getKbnSkns(sknTitle);
  }

  getCarSelection(uketsukeExtendsId: number): Promise<any> {
    return this.reqService.getCarSelection(uketsukeExtendsId);
  }

  saveInspectLineResult(data: InspectLineResult): Promise<any> {
    return this.reqService.saveInspectLineResult(data);
  }

  saveInspectLineSeibi(data: InspectDescriptions[]): Promise<any> {
    return this.reqService.saveInspectLineSeibi(data);
  }

  saveCancel(data: any): Promise<any> {
    return this.reqService.saveCancel(data);
  }

  getReservationTarget(uketsukeExtendsId: number): Promise<any> {
    return this.reqService.getReservationTarget(uketsukeExtendsId, 0);
  }

  /*
   * WHY: inspectLine.vue に初期表示の並び替え、光軸表示値、store 復元判定を持たせると
   * API 応答の形を画面が直接知るため、service 側で初期 state に変換する。
   */
  buildInitialResultState(
    data: InspectLineResult[] | null | undefined,
    stateList: InspectLineResult[]
  ): InspectLineInitialResultState {
    const inspectLineList = Array.isArray(data)
      ? data.slice().sort((a: any, b: any) =>
          a.inspectLineCd > b.inspectLineCd ? 1 : -1
        )
      : [];
    const headLightLuminance = this.resolveHeadLightLuminance(inspectLineList[8]);
    const lightRightLuminance = this.resolveHeadLightLuminance(inspectLineList[9]);
    const arrInspected = inspectLineList.filter(e => e.inspectResult !== 0);
    const shouldSyncState =
      arrInspected.length > 0 &&
      stateList &&
      arrInspected.length < stateList.length;
    const syncItems: InspectLineResult[] = [];

    if (shouldSyncState) {
      stateList.forEach((item: InspectLineResult, index: number) => {
        if (JSON.stringify(item) != JSON.stringify(inspectLineList[index])) {
          inspectLineList[index] = item;
          syncItems.push(item);
        }
      });
    }

    return {
      hasData: inspectLineList.length > 0,
      inspectLineList,
      headLightLuminance,
      lightRightLuminance,
      syncItems
    };
  }

  /*
   * WHY: WorkAndBuhin に渡す speed/diesel の抽出位置は API 応答構造に依存するため、
   * 画面ではなく service が inspectLineSeibi の読み取り方を保持する。
   */
  buildSeibiState(data: any): InspectLineSeibiState {
    const workInspects = resolveInspectLineWorkInspects(data);
    return {
      inspectData: data || [],
      speedInspect: workInspects.speedInspect,
      dieselInspect: workInspects.dieselInspect
    };
  }

  /*
   * WHY: SpeedMeter/Diesel の分岐と WorkAndBuhin 用 showWork 付与を画面から隠し、
   * inspectLine.vue は選択イベントの制御だけに集中させる。
   */
  buildWorkBuhinData(
    inspectType: InspectLineWorkType,
    params: {
      speedInspect: InspectDescriptions;
      dieselInspect: InspectDescriptions;
    }
  ): any {
    const inspect = resolveInspectLineWorkInspect(inspectType, params);
    return buildInspectLineWorkBuhinData(inspect);
  }

  /*
   * WHY: WorkAndBuhin 確定時に speed/diesel のどちらへ結果を戻すかは業務分岐なので、
   * activeInspect の判定を service 内に閉じ込める。
   */
  buildConfirmApplyState(
    activeInspect: any,
    params: {
      speedInspect: InspectDescriptions;
      dieselInspect: InspectDescriptions;
    }
  ): InspectLineConfirmApplyState {
    return buildInspectLineConfirmState(activeInspect, params);
  }

  /*
   * WHY: 検査ライン結果と整備データの同期条件を画面に置くと保存順制御と混ざるため、
   * 保存前に適用すべき state の生成だけを service が担う。
   */
  buildSeibiSyncApplyState(
    inspect: InspectDescriptions,
    inspectLineResult: InspectLineResult
  ): InspectLineSeibiSyncApplyState {
    return buildInspectLineSeibiSyncState(inspect, inspectLineResult);
  }

  /*
   * WHY: スピードメーター値入力 dialog の戻り値を inspectLine 保存形式へ変換する責務を
   * dialog 制御から切り離す。
   */
  buildValueConfirmApplyState(
    inspectValueList: any[] | null | undefined,
    inspectResult: number | undefined
  ): InspectLineValueConfirmApplyState {
    return buildInspectLineValueConfirmState(inspectValueList, inspectResult);
  }

  /*
   * WHY: 完了判定は WorkAndBuhin の選択状態も含むため、template から判定条件を隠す。
   */
  hasSpeedMeterCompleted(
    speedInspect: InspectDescriptions,
    speedInspectLineResult: InspectLineResult
  ): boolean {
    return hasInspectLineSpeedMeterCompleted(speedInspect, speedInspectLineResult);
  }

  /*
   * WHY: 検査結果入力済み判定を template へ散らさず、service の表示判定に集約する。
   */
  hasResult(item: InspectLineResult): boolean {
    return hasInspectLineResult(item);
  }

  /*
   * WHY: スピードメーター値の有無は入力データ構造に依存するため、画面の表示条件から隠す。
   */
  hasSpeedValue(speedInspect: InspectDescriptions): boolean {
    return hasInspectLineSpeedValue(speedInspect);
  }

  /*
   * WHY: ディーゼル黒煙の完了条件を page から分離し、WorkAndBuhin 選択状態込みで判定する。
   */
  hasDieselCompleted(
    dieselInspect: InspectDescriptions,
    dieselInspectLineResult: InspectLineResult
  ): boolean {
    return hasInspectLineDieselCompleted(dieselInspect, dieselInspectLineResult);
  }

  /*
   * WHY: ボタン文言は workBuhinSelected の業務値に依存するため、template に数値条件を出さない。
   */
  getWorkButtonLabel(inspect: InspectDescriptions): string {
    return getInspectLineWorkButtonLabel(inspect);
  }

  private resolveHeadLightLuminance(item: InspectLineResult | undefined): number {
    if (!item) {
      return 0;
    }
    if (item.inspectResult == 2) {
      return item.isMigaki == 1 ? 2 : 3;
    }
    return item.inspectResult;
  }
}
